/*
 * Get ready — `getready.list` (App-shell canon §4, FLEXIBLE).
 *
 * Transcribed from the canon §4.1–§4.3 + §1.3 DOM ids + §4 testid table; the
 * DC mock (App Shell.dc.html readyEvents view) is the styling reference.
 * Canon wins on conflict.
 *
 * DATA (Ruling A + C, BOARDROOM-2026-08-09-133534):
 *  - The SHIPPED list renders ONLY live data: the real events the signed-in
 *    participant holds a place in (my-api /me/holdings merged with box-office
 *    orders), with an honest empty state when there are none. The canon's
 *    5-event [demo] dataset (src/data/seed.ts) is a TEST FIXTURE only.
 *  - The server is the readiness AUTHORITY: step states come from the
 *    server's readiness result via the src/data/getReady.ts seam. No
 *    client-persisted completion flag exists — the retired
 *    markProfileComplete pattern is gone, and the previous client-side
 *    profile computation (participantConfig + computeReadySteps) is no
 *    longer consulted for display.
 *
 * TICKET VISIBILITY (assign-cascade fix, dispatch BOARDROOM-2026-08-11-081455):
 * a card appears here ONLY for events the account EXPLICITLY holds a place in —
 * my-api /me/holdings (a self-assign the buyer made via the assign flow, or an
 * invitee's claim). Get ready NEVER auto-claims purchased tickets: the previous
 * "lazily ensure a self-holder for every purchased ticket on open" loop
 * silently self-allocated all of a buyer's tickets (the 6-of-8-assigned-to-You
 * corruption Duncan hit) and has been REMOVED. A purchased-but-unassigned
 * ticket lives on the Orders screen until the buyer assigns it.
 *
 * TODO(V1.1 kids amendment §12.2): guardian/child-subject cards (Junior Fun
 * Run pattern — "Mia's profile", guardian footer note, child wizard routes)
 * are deferred with the managed-child wizard.
 */
import { createContext, useContext, useEffect, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { LiveDataNotice, useData } from '../data/DataProvider'
import { NotWired } from '../components/NotWired'
import { useShellChrome } from '../components/AppShell'
import { loadSession } from '../auth/session'
import { startSignIn } from '../auth/startSignIn'
import {
  cardProgress, cardState, fetchEventSteps, heatIssuanceNote, heatPolicyFromReadiness, nextUpLine,
  statusPill, stepEntryPath, stepsFromTicketReadiness, subscribeReadinessRefresh,
  type GetReadyEvent, type GetReadyStep,
} from '../data/getReady'
import { fetchAllReadiness, fetchMyHoldings, MyApiError, myApiBase, type Holding, type Readiness } from '../data/myApi'
import { DEFAULT_WIZARD_CONFIG } from './wizard/wizardConfig'
import { firstIncompleteStep, EMPTY_DRAFT } from './wizard/wizardModel'
import { loadSelfDraft } from './wizard/profileStore'
import { HeatSelect } from './HeatSelect'

// ---------------------------------------------------------------------------
// Step row — canon §4.3 state machine (DC transcription)
// ---------------------------------------------------------------------------

function StepRowView({ event, step, onAction }: {
  event: GetReadyEvent
  step: GetReadyStep
  onAction?: (event: GetReadyEvent, step: GetReadyStep) => void
}) {
  const { isMobile } = useShellChrome()
  const done = step.state === 'done'
  const now = step.state === 'now'
  const soon = step.state === 'soon'

  // Right column: CTA when the step has a wired flow (`now` → accent
  // Start/Resume/Sign/Add; `done` → outline Edit/View); tag otherwise for
  // soon/system rows. System steps NEVER get a CTA (canon §4.3 must-nots).
  const showCta = !step.system && !!step.cta && (now || done)
  const hasTag = (soon && !step.system) || !!step.system
  const tagText = step.system
    ? (isMobile ? 'AUTO' : 'WE DO THIS')
    : (isMobile ? 'SOON' : 'SCHEDULED')

  const rowStyle: CSSProperties = {
    display: 'grid',
    // {28|38}px gutter (connector + icon) / text / action-or-tag (§4.3)
    gridTemplateColumns: `${isMobile ? 28 : 38}px minmax(0,1fr) auto`,
    columnGap: isMobile ? 7 : 9,
    alignItems: 'center',
    padding: isMobile ? '9px 8px 9px 0' : '11px 14px 11px 0',
    borderTop: '1px solid var(--hairline-06)',
    // [token] now rows get bg rgba(230,193,90,.06)
    background: now ? 'var(--tint-warn-row)' : undefined,
  }
  const connStyle: CSSProperties = {
    width: isMobile ? 8 : 14, height: 2, flex: 'none', borderRadius: '0 2px 2px 0',
    background: event.organiser.accentFg, opacity: soon ? 0.4 : 0.95,
  }
  const iconStyle: CSSProperties = {
    width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, flex: 'none', borderRadius: '50%',
    display: 'grid', placeItems: 'center', fontSize: isMobile ? 11 : 12, fontWeight: 800,
    // [token] step icon tints per the §4.3 state table
    ...(done
      ? { background: 'var(--tint-teal-icon)', color: 'var(--success)' }
      : now
        ? { background: 'var(--tint-warn-icon)', color: 'var(--warn)' }
        : { background: 'rgba(255,255,255,.06)', color: 'var(--text-dim)' }),
  }
  const ctaStyle: CSSProperties = {
    flex: 'none', borderRadius: 9, padding: `0 ${isMobile ? 11 : 14}px`, minHeight: 'var(--tap-min)',
    // EQUAL-WIDTH step CTAs (CMT-2026-08-10-019 / -043): the padding-only button
    // otherwise sizes to its label, so Start/Sign render narrower than Choose.
    // A shared min-width (fits the widest CTA label) + centred text makes every
    // get-ready step CTA the same width per the Claude design.
    minWidth: isMobile ? 74 : 88, display: 'grid', placeItems: 'center', textAlign: 'center',
    fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer',
    ...(done
      ? { border: '1px solid var(--hairline-16)', background: 'transparent', color: 'var(--text-softbtn)' }
      : { border: 0, background: 'var(--accent)', color: 'var(--accent-ink)' }),
  }

  return (
    <div id={`getready.eventcard.${event.id}.steps.${step.key}`} data-testid={`getready-step-${step.key}`} style={rowStyle}>
      <div style={{ gridColumn: 1, gridRow: '1 / span 2', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden style={connStyle} />
        <span data-testid="getready-step-icon" style={iconStyle}>{done ? '✓' : now ? '!' : '○'}</span>
      </div>
      <div
        data-testid="getready-step-label"
        style={{ gridColumn: 2, gridRow: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: soon ? 'var(--text-muted)' : 'var(--text)' }}
      >
        {step.label}
      </div>
      <div
        data-testid="getready-step-meta"
        style={{ gridColumn: 2, gridRow: 2, minWidth: 0, marginTop: 1, fontSize: 11.5, ...(now ? { color: 'var(--warn)', fontWeight: 600 } : { color: 'var(--text-faint)' }) }}
      >
        {step.meta}
      </div>
      {showCta && (
        <button
          data-testid="getready-step-cta"
          onClick={() => onAction?.(event, step)}
          style={{ ...ctaStyle, gridColumn: 3, gridRow: '1 / span 2' }}
        >
          {step.cta}
        </button>
      )}
      {!showCta && hasTag && (
        <span
          data-testid="getready-step-tag"
          className="mono"
          style={{
            gridColumn: 3, gridRow: '1 / span 2', fontSize: 9, letterSpacing: '.08em',
            color: 'var(--text-dim)', border: '1px solid var(--hairline-12)', borderRadius: 5, padding: '2px 5px',
          }}
        >
          {tagText}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Event card — canon §4.2 component tree
// ---------------------------------------------------------------------------

function EventCard({ event, onAction }: {
  event: GetReadyEvent
  onAction?: (event: GetReadyEvent, step: GetReadyStep) => void
}) {
  const { isMobile } = useShellChrome()
  const state = cardState(event)
  const pill = statusPill(event)
  const { doneN, yoursN, pct } = cardProgress(event)
  const nextUp = nextUpLine(event)

  const pillStyle: CSSProperties = {
    flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    borderRadius: 'var(--r-pill)', padding: '3px 9px', fontSize: 10.5, fontWeight: 800,
    ...(pill.tone === 'warn'
      ? { background: 'rgba(230,193,90,.16)', color: 'var(--warn)' }
      : { background: 'rgba(18,196,172,.16)', color: 'var(--success)' }),
  }
  const pillNode = (
    <span data-testid="getready-card-status" style={pillStyle}>{`${pill.icon} ${pill.text}`}</span>
  )

  return (
    <div
      id={`getready.eventcard.${event.id}`}
      data-testid="getready-eventcard"
      data-event-id={event.id}
      style={{
        background: 'var(--surface)', border: '1px solid var(--hairline-12)',
        // [token] card left border 3px in the organiser accent
        borderLeft: `3px solid ${event.organiser.accentFg}`,
        borderRadius: 'var(--r-card)', overflow: 'hidden', marginBottom: 18,
        boxShadow: 'var(--shadow-card)',
      }}
    >
      {/* header */}
      <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,.045)', borderBottom: '1px solid var(--hairline-10)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {event.organiser.name && (
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.11em', textTransform: 'uppercase', color: event.organiser.accentFg, marginBottom: 3 }}>
                {event.organiser.name}
              </div>
            )}
            <div style={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.2 }}>{event.name}</div>
            {event.when && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{event.when}</div>}
          </div>
          {/* status pill right + pct text — desktop only (§4.2) */}
          {!isMobile && (
            <>
              {pillNode}
              <span data-testid="getready-card-pct" style={{ flex: 'none', whiteSpace: 'nowrap', fontSize: 11.5, color: 'var(--text-muted)' }}>
                {`${doneN} of ${yoursN} done`}
              </span>
            </>
          )}
        </div>
        {/* mobile: pill wraps below the title; pct text hidden (§4.2) */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>{pillNode}</div>
        )}
        <div style={{ height: 'var(--progress-h)', borderRadius: 'var(--r-pill)', background: 'var(--progress-track)', overflow: 'hidden', marginTop: 8 }}>
          <div data-testid="getready-card-progress" style={{ height: '100%', borderRadius: 'var(--r-pill)', background: 'var(--accent)', width: `${pct}%` }} />
        </div>
        {nextUp && (
          <div data-testid="getready-card-nextup" style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4, marginTop: 7 }}>
            {nextUp}
          </div>
        )}
      </div>

      {/* steps */}
      <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.1em', color: 'var(--text-dim)', padding: '11px 16px 2px' }}>
        YOUR STEPS FOR THIS EVENT
      </div>
      <div>
        {event.steps.map(step => (
          <StepRowView key={step.key} event={event} step={step} onAction={onAction} />
        ))}
      </div>

      {/* all-done banner (all-done only — canon §4.3 acceptance: the
          yours-done card shows next-up instead) */}
      {state === 'all-done' && (
        <div
          data-testid="getready-card-alldone"
          style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 16px', borderTop: '1px solid rgba(18,196,172,.28)', background: 'rgba(18,196,172,.09)' }}
        >
          <span aria-hidden style={{ width: 26, height: 26, flex: 'none', borderRadius: '50%', background: 'var(--accent)', color: 'var(--accent-ink)', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 800 }}>✓</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* all-done ⇔ EVERY step (incl. system) is done ⇒ the ticket IS
                issued and sitting in the wallet — so instead of the old
                dead-end "we'll take it from here", point the participant
                straight at it (BOARDROOM-2026-08-14-100859 UX #5). */}
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--success)' }}>{"You're all set"}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
              {'Your ticket is in your wallet. '}
              <Link
                data-testid="getready-card-alldone-cta"
                to="/tickets"
                style={{ color: 'var(--accent-bright)', fontWeight: 700, textDecoration: 'underline' }}
              >
                View it in Tickets
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* footer note (canon §4.2). A server-provided note wins; otherwise, while
          the required heat step is still pending, the card derives the verbatim
          issuance line from its steps so the live pre-heat card always carries
          it (audit D4 — the live path never supplies a per-event note). */}
      {(event.note ?? heatIssuanceNote(event)) && (
        <div data-testid="getready-card-note" style={{ padding: '11px 16px', borderTop: '1px solid var(--hairline-06)', fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.45 }}>
          {event.note ?? heatIssuanceNote(event)}
        </div>
      )}
    </div>
  )
}

/** Presentational card list (max-width 700px column, §4.1). Rendered by the
 *  live container below; unit tests render it with the seed FIXTURE. */
export function GetReadyList({ events, onStepAction }: {
  events: GetReadyEvent[]
  onStepAction?: (event: GetReadyEvent, step: GetReadyStep) => void
}) {
  return (
    <div style={{ maxWidth: 'var(--content-max)' }}>
      {events.map(e => (
        <EventCard key={e.id} event={e} onAction={onStepAction} />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live data — real held events + server-derived steps (Rulings A + C)
// ---------------------------------------------------------------------------

interface LiveGetReady {
  /** Distinct guard states with honest, non-conflated copy (audit D7):
   *  - 'unconfigured' → no VITE_MY_API_BASE (a genuine build gap → NotWired)
   *  - 'signed-out'   → no session, or a 401/403 mid-session (sign-in prompt)
   *  - 'unavailable'  → a network/5xx fetch failure (service not responding) */
  state: 'unconfigured' | 'signed-out' | 'loading' | 'ready' | 'unavailable'
  events: GetReadyEvent[]
  /** eventId → the held ticket backing that card (waiver/heat flows need it). */
  ticketByEvent: Record<string, string>
}

/** A thrown my-api error that means the session is no longer valid (401/403)
 *  → maps to 'signed-out', never the "service unavailable" copy (audit D7). */
function isAuthFailure(err: unknown): boolean {
  return err instanceof MyApiError && (err.status === 401 || err.status === 403)
}

/** Exported for the Tickets wallet (canon §8): issuance derives from the SAME
 *  live source of truth as Get ready (§2.4 single-source rule / Ruling C). */
export function useLiveGetReadyEvents(): LiveGetReady {
  const { liveState, eventById } = useData()
  const [out, setOut] = useState<LiveGetReady>({ state: 'unconfigured', events: [], ticketByEvent: {} })
  // A profile/waiver save bumps this via the readiness-refresh bus so the
  // subscription re-reads server readiness on return to Get ready — otherwise
  // the effect only re-runs on liveState/tickets changes and shows a stale
  // pre-save snapshot (CMT-043 live-test bug).
  const [refreshTick, setRefreshTick] = useState(0)
  useEffect(() => subscribeReadinessRefresh(() => setRefreshTick((t) => t + 1)), [])

  useEffect(() => {
    const base = myApiBase()
    const session = loadSession()
    // Split the three distinct guard states (audit D7): no API base is a
    // genuine build gap; no session is a sign-out (never "build unwired").
    if (!base) {
      setOut({ state: 'unconfigured', events: [], ticketByEvent: {} })
      return
    }
    if (!session) {
      setOut({ state: 'signed-out', events: [], ticketByEvent: {} })
      return
    }
    if (liveState !== 'ready') {
      setOut({ state: 'loading', events: [], ticketByEvent: {} })
      return
    }
    let cancelled = false
    setOut(o => ({ ...o, state: 'loading' }))
    ;(async () => {
      // Held places come SOLELY from my-api holdings (assign-cascade fix): a
      // card appears here only for a place the account EXPLICITLY holds — a
      // buyer's own self-assign, or an invitee's claim. Opening Get ready never
      // auto-claims purchased tickets (the removed ensureSelfHolder-over-all
      // loop). A holdings fetch failure is a genuine outage → the outer catch.
      const holdings = await fetchMyHoldings(session.idToken, base)

      // One card per EVENT (canon §4). A participant holds one place per event
      // in V1; if several, the first held ticket backs the card.
      const byEvent = new Map<string, Holding>()
      for (const h of holdings) if (!byEvent.has(h.eventId)) byEvent.set(h.eventId, h)

      // PERF (CMT-150431): ONE batched readiness call for every holding replaces
      // the per-ticket readiness N+1 that made large orders (~18 tickets) hang /
      // "not respond". Build a ticketId→readiness map. A 404 means an older
      // my-api WITHOUT /me/readiness → fall back to the per-holding fetchEventSteps
      // fan-out below (readinessByTicket stays null), so the FE degrades
      // gracefully until the my-api change deploys. Any OTHER error is a genuine
      // outage → rethrow to the outer catch (auth→signed-out, else unavailable).
      let readinessByTicket: Map<string, Readiness> | null = null
      if (byEvent.size > 0) {
        try {
          const all = await fetchAllReadiness(session.idToken, base)
          readinessByTicket = new Map(all.map((r) => [r.ticketId, r.readiness]))
        } catch (err) {
          // The batched endpoint is a PERF optimization ONLY: ANY failure — a 404
          // on an older my-api, a 500 (stage /me/readiness 500'd right after the
          // readiness deploy), a timeout — must degrade to the per-ticket fan-out
          // below and NEVER blank the whole Get-ready page. Only a genuine auth
          // failure propagates (→ signed-out). Previously this re-threw on any
          // non-404, so a batched 500 collapsed the entire page to "unavailable".
          if (isAuthFailure(err)) throw err
          readinessByTicket = null // batched unavailable → per-holding fallback
        }
      }

      // RESILIENCE (STEP 4): build every event's card IN PARALLEL and let a
      // single slow/failed per-event read degrade to skipping THAT card only —
      // one failing/slow sub-call must never blank the whole page (Duncan's
      // ~35s-hang collapse). In the batched path the readiness is already in
      // hand (no per-card round-trip); only a ticket missing from the batch (a
      // rare read race) or the 404 fallback re-fetches via fetchEventSteps. The
      // readiness→steps derivation is IDENTICAL either way (stepsFromTicketReadiness
      // + heatPolicyFromReadiness — same as fetchEventSteps), so behaviour is
      // unchanged, just fewer round-trips.
      const results = await Promise.allSettled(
        [...byEvent.values()].map(async (h): Promise<{ event: GetReadyEvent; ticketId: string }> => {
          const ev = eventById(h.eventId)
          const batched = readinessByTicket?.get(h.ticketId)
          const { steps, eventStartsAt, selectedHeatTime } = batched
            ? {
                steps: stepsFromTicketReadiness(batched, {
                  organiserName: ev?.organiser.name,
                  heatPolicy: heatPolicyFromReadiness(batched),
                }),
                eventStartsAt: batched.eventStartsAt ?? null,
                selectedHeatTime: batched.selectedHeatTime ?? null,
              }
            : await fetchEventSteps(session.idToken, base, h.ticketId, { organiserName: ev?.organiser.name })
          return {
            event: {
              id: h.eventId,
              name: ev?.name ?? h.eventName,
              // Org accent comes from the central Trumin asset store per org-unit
              // in production (canon §2.1); the api.ts default is the app accent.
              organiser: { name: ev?.organiser.name ?? '', accentFg: ev?.organiser.accentFg ?? 'var(--accent-bright)' },
              when: ev?.when ?? '',
              eventStartsAt,
              selectedHeatTime,
              steps,
            },
            ticketId: h.ticketId,
          }
        }),
      )
      const okResults = results.filter(
        (r): r is PromiseFulfilledResult<{ event: GetReadyEvent; ticketId: string }> => r.status === 'fulfilled',
      )
      // Every per-event read failed while we DID hold places — surface the
      // honest outage/sign-in state (via the outer catch: auth→signed-out, else
      // unavailable) rather than a misleading "nothing to get ready for" empty.
      if (byEvent.size > 0 && okResults.length === 0) {
        throw (results.find(r => r.status === 'rejected') as PromiseRejectedResult).reason
      }
      // Chronological order — the soonest-arriving event at the TOP so the user
      // is drawn to the one they most likely need to get ready for first
      // (CMT-121335). Sort by eventStartsAt ascending; events with no known date
      // sink to the bottom. Stable for equal/absent dates.
      const events: GetReadyEvent[] = okResults
        .map(r => r.value.event)
        .sort((a, b) => {
          const ta = a.eventStartsAt ? Date.parse(a.eventStartsAt) : NaN
          const tb = b.eventStartsAt ? Date.parse(b.eventStartsAt) : NaN
          const va = Number.isNaN(ta) ? Infinity : ta
          const vb = Number.isNaN(tb) ? Infinity : tb
          return va - vb
        })
      const ticketByEvent: Record<string, string> = {}
      // PR#22 parallel version already calls fetchEventSteps per event inside the
      // Promise.allSettled map above (line ~361) — which is where #19's heat-policy
      // derivation (event_heat_policy_v1 → heat_sel/heat_policy) lives, inside the
      // getReady.ts seam — so the parallel path preserves that behaviour AND adds
      // one-slow-event resilience. Only the ticketByEvent map is left to populate.
      for (const r of okResults) ticketByEvent[r.value.event.id] = r.value.ticketId
      if (!cancelled) setOut({ state: 'ready', events, ticketByEvent })
    })().catch(err => {
      if (cancelled) return
      // A 401/403 mid-session is an expired/invalid session, not an outage
      // (audit D7) — show the sign-in prompt, not "service not responding".
      setOut({ state: isAuthFailure(err) ? 'signed-out' : 'unavailable', events: [], ticketByEvent: {} })
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveState, refreshTick])

  return out
}

// ---------------------------------------------------------------------------
// Shared live provider (audit D8) — one subscription the SHELL badge and the
// Get-ready screen both read, so the nav badge derives from the SAME live
// non-system `now`-step count as the card pills (canon §2.4). Lifting it above
// AppShell is what lets the shell render a badge the screen data drives.
// ---------------------------------------------------------------------------

const GetReadyLiveContext = createContext<LiveGetReady | null>(null)

/** Provides one shared `useLiveGetReadyEvents()` result. Placed above AppShell
 *  (inside DataProvider) so both the shell badge and the screen consume it. */
export function GetReadyLiveProvider({ children }: { children: ReactNode }) {
  const live = useLiveGetReadyEvents()
  return <GetReadyLiveContext.Provider value={live}>{children}</GetReadyLiveContext.Provider>
}

/** Read the shared live Get-ready result. MUST be used within a
 *  GetReadyLiveProvider (App wraps the shell; tests wrap the screen). */
export function useGetReadyLive(): LiveGetReady {
  const ctx = useContext(GetReadyLiveContext)
  if (!ctx) throw new Error('useGetReadyLive must be used within a GetReadyLiveProvider')
  return ctx
}

/** Non-throwing reader for callers rendered under the provider in production but
 *  standalone in tests (e.g. WaiverFlow deriving the receipt's next action).
 *  Returns null outside a provider rather than throwing. */
export function useGetReadyLiveOptional(): LiveGetReady | null {
  return useContext(GetReadyLiveContext)
}

/** Honest signed-out / expired-session guard (audit D7): a signed-out user is
 *  NEVER told the build is unwired — they see a sign-in prompt + affordance. */
function SignInNotice() {
  const [busy, setBusy] = useState(false)
  return (
    <div
      role="status"
      data-testid="getready-signin"
      style={{
        border: '1px solid var(--hairline-12)', borderRadius: 'var(--r-card, 16px)',
        padding: 20, marginTop: 8, maxWidth: 'var(--content-max)',
        color: 'var(--text-secondary)', background: 'var(--surface)', fontSize: 13,
      }}
    >
      <p style={{ margin: '0 0 12px' }}>Please sign in to see your event steps.</p>
      <button
        data-testid="getready-signin-cta"
        onClick={() => { setBusy(true); void startSignIn().then(started => { if (!started) setBusy(false) }) }}
        disabled={busy}
        style={{
          minHeight: 'var(--tap-min)', borderRadius: 9, padding: '0 16px', border: 0,
          background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit',
          fontSize: 13, fontWeight: 800, cursor: 'pointer',
        }}
      >
        {busy ? 'Opening sign-in…' : 'Sign in'}
      </button>
    </div>
  )
}

/** Resume opens the wizard at the first incomplete step (`pAt` — canon §4.3
 *  E2E contract), resolved from the subject's real stored profile. */
async function resolveResumeStep(): Promise<number> {
  try {
    const draft = await loadSelfDraft()
    return firstIncompleteStep(draft ?? EMPTY_DRAFT, DEFAULT_WIZARD_CONFIG.genderPolicy)
  } catch {
    return 1
  }
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function GetReady() {
  const { liveState } = useData()
  const { state, events, ticketByEvent } = useGetReadyLive()
  const { showPageHead } = useShellChrome()
  const navigate = useNavigate()

  const onStepAction = (event: GetReadyEvent, step: GetReadyStep) => {
    void (async () => {
      const resumeStep = step.key === 'profile' && step.cta === 'Resume' ? await resolveResumeStep() : undefined
      const path = stepEntryPath(event.id, step, { resumeStep })
      if (!path) return
      // Waiver/heat surfaces need the backing ticket; carried as route state
      // until the canonical flows own their own lookups.
      navigate(path, { state: { ticketId: ticketByEvent[event.id], eventName: event.name } })
    })()
  }

  return (
    <div id="getready.root">
      {/* Desktop-only page head (§4.1) */}
      {showPageHead && (
        <div id="getready.head" data-testid="getready-head">
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 4px' }}>Get ready</h1>
          {/* Verbatim [chrome] sub (canon §4.1). */}
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            {'Do what you can now — the rest unlocks on its own date.'}
          </p>
        </div>
      )}
      {state === 'signed-out' ? (
        // Honest sign-out copy — checked FIRST so a signed-out / expired user
        // is never shown the orders "not connected in this build" panel or the
        // my-api NotWired panel (audit D7).
        <SignInNotice />
      ) : liveState !== 'ready' ? (
        <LiveDataNotice liveState={liveState} />
      ) : state === 'unconfigured' ? (
        <NotWired service="The get-ready allocation/readiness service (my-api)" />
      ) : state === 'loading' ? (
        <p className="secondary" style={{ fontSize: 13 }} role="status">Loading your events&hellip;</p>
      ) : state === 'unavailable' ? (
        <p className="secondary" role="alert" style={{ fontSize: 13 }}>
          The get-ready service isn&rsquo;t responding right now &mdash; try again shortly.
        </p>
      ) : events.length === 0 ? (
        // CMT-2026-08-14-034: no held places ⇒ nothing to get ready for. Don't
        // dead-end the purchaser on an empty panel — send them to Orders, where
        // their unassigned purchases live and the next action (assign) actually
        // is. Guarded to the CONFIRMED-empty ready state only (the signed-out /
        // unconfigured / loading / unavailable branches are handled above), so a
        // slow or down my-api never mis-redirects. `replace` keeps the back
        // button from bouncing straight back into this redirect.
        <Navigate to="/orders" replace />
      ) : (
        <GetReadyList events={events} onStepAction={onStepAction} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Flow-entry routes (temporary wrappers)
// ---------------------------------------------------------------------------

/** `/get-ready/heat/:eventId` — NON-CANON temporary route for the
 *  participant-chosen heat step (see the deviation note in
 *  src/data/getReady.ts stepEntryPath): the canon models heat allocation as
 *  system-automatic, today's my-api requires a choice. Removed when the
 *  server-derived step payload settles the conflict. */
export function HeatEntry() {
  const navigate = useNavigate()
  const location = useLocation()
  const { eventId } = useParams()
  const routeState = (location.state ?? {}) as { ticketId?: string; eventName?: string }
  const base = myApiBase()
  const session = loadSession()
  if (!routeState.ticketId || !base || !session || !eventId) return <Navigate to="/get-ready" replace />
  return (
    <HeatSelect
      eventName={routeState.eventName ?? 'Your event'}
      eventId={eventId}
      idToken={session.idToken}
      base={base}
      ticketId={routeState.ticketId}
      onBack={() => navigate('/get-ready')}
      onAssigned={() => navigate('/get-ready')}
    />
  )
}
